<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Services</span>
            </nav>
            <h1 class="page-title">Our Services</h1>
            <p class="page-subtitle">Access all county services from one secure platform</p>
          </div>

          <!-- Search Bar -->
          <div class="hero-search">
            <div class="search-input-wrapper">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Search services..."
                  class="search-input"
              />
              <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Category Filter -->
      <section class="filter-section">
        <div class="container">
          <div class="filter-tabs">
            <button
                v-for="category in categories"
                :key="category.id"
                class="filter-tab"
                :class="{ active: activeCategory === category.id }"
                @click="activeCategory = category.id"
            >
              <component :is="category.icon" />
              {{ category.name }}
              <span class="tab-count">{{ getCategoryCount(category.id) }}</span>
            </button>
          </div>
        </div>
      </section>

      <!-- Services Grid -->
      <section class="services-section">
        <div class="container">
          <!-- Results Info -->
          <div class="results-info" v-if="searchQuery || activeCategory !== 'all'">
            <span>Showing {{ filteredServices.length }} service{{ filteredServices.length !== 1 ? 's' : '' }}</span>
            <button v-if="searchQuery || activeCategory !== 'all'" @click="clearFilters" class="clear-filters">
              Clear filters
            </button>
          </div>

          <!-- Services Grid -->
          <div class="services-grid" v-if="filteredServices.length > 0">
            <div
                v-for="service in filteredServices"
                :key="service.id"
                class="service-card"
                :class="service.featured ? 'featured' : ''"
            >
              <div class="service-badge" v-if="service.badge">{{ service.badge }}</div>
              <div class="service-icon" :class="service.color">
                <component :is="service.icon" />
              </div>
              <h3>{{ service.title }}</h3>
              <p>{{ service.description }}</p>
              <ul class="service-features">
                <li v-for="(feature, index) in service.features" :key="index">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  {{ feature }}
                </li>
              </ul>
              <router-link :to="service.link" class="service-link">
                {{ service.linkText || 'Get Started' }}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </router-link>
            </div>
          </div>

          <!-- No Results -->
          <div v-else class="no-results">
            <div class="no-results-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
            <h3>No services found</h3>
            <p>Try adjusting your search or filter to find what you're looking for.</p>
            <button @click="clearFilters" class="btn btn-primary">
              View All Services
            </button>
          </div>
        </div>
      </section>

      <!-- Quick Access Section -->
      <section class="quick-access-section">
        <div class="container">
          <div class="section-header">
            <span class="section-badge">Quick Access</span>
            <h2 class="section-title">Popular Services</h2>
            <p class="section-subtitle">Most frequently accessed services by our users</p>
          </div>

          <div class="quick-access-grid">
            <router-link to="/pay-bill" class="quick-access-card">
              <div class="qa-icon blue">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
              </div>
              <div class="qa-content">
                <h4>Pay Bill</h4>
                <p>Quick bill lookup & payment</p>
              </div>
              <svg class="qa-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </router-link>

            <router-link to="/print-receipt" class="quick-access-card">
              <div class="qa-icon green">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <polyline points="6 9 6 2 18 2 18 9"></polyline>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                  <rect x="6" y="14" width="12" height="8"></rect>
                </svg>
              </div>
              <div class="qa-content">
                <h4>Print Receipt</h4>
                <p>Retrieve & print receipts</p>
              </div>
              <svg class="qa-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </router-link>

            <router-link to="/daily-parking" class="quick-access-card">
              <div class="qa-icon purple">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="1" y="3" width="15" height="13" rx="2"></rect>
                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                  <circle cx="12.5" cy="18.5" r="2.5"></circle>
                  <path d="M16 8h4l3 5v5h-2"></path>
                  <circle cx="19.5" cy="18.5" r="2.5"></circle>
                </svg>
              </div>
              <div class="qa-content">
                <h4>Daily Parking</h4>
                <p>Pay for parking instantly</p>
              </div>
              <svg class="qa-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </router-link>

            <router-link to="/land-rates" class="quick-access-card">
              <div class="qa-icon orange">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <div class="qa-content">
                <h4>Land Rates</h4>
                <p>Pay property taxes</p>
              </div>
              <svg class="qa-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </router-link>
          </div>
        </div>
      </section>

      <!-- Stats Section -->
      <section class="stats-section">
        <div class="container">
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">19+</div>
              <div class="stat-label">Services Available</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-number">24/7</div>
              <div class="stat-label">Online Access</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-number">2min</div>
              <div class="stat-label">Average Time</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-number">100%</div>
              <div class="stat-label">Secure Payments</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Help Section -->
      <section class="help-section">
        <div class="container">
          <div class="help-card">
            <div class="help-content">
              <h2>Need Help Finding a Service?</h2>
              <p>Our support team is ready to assist you with any questions about our services.</p>
              <div class="help-actions">
                <router-link to="/contact" class="btn btn-white">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  Contact Support
                </router-link>
                <router-link to="/how-it-works" class="btn btn-outline-white">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polygon points="10 8 16 12 10 16 10 8"></polygon>
                  </svg>
                  How It Works
                </router-link>
              </div>
            </div>
            <div class="help-graphic">
              <div class="graphic-circles">
                <div class="circle circle-1"></div>
                <div class="circle circle-2"></div>
                <div class="circle circle-3"></div>
              </div>
              <div class="graphic-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { enabledMap as moduleEnabledMap, loadModules as loadSSModules, slugForPath } from "@/api/moduleRegistry";

// Icon Components
const BusinessIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>`
};

const HomeIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9 22 9 12 15 12 15 22"></polyline>
  </svg>`
};

const CarIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="1" y="3" width="15" height="13" rx="2"></rect>
    <circle cx="5.5" cy="18.5" r="2.5"></circle>
    <circle cx="12.5" cy="18.5" r="2.5"></circle>
    <path d="M16 8h4l3 5v5h-2"></path>
    <circle cx="19.5" cy="18.5" r="2.5"></circle>
  </svg>`
};

const DocumentIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
  </svg>`
};

const PrinterIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <polyline points="6 9 6 2 18 2 18 9"></polyline>
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
    <rect x="6" y="14" width="12" height="8"></rect>
  </svg>`
};

const BuildingIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
    <line x1="12" y1="22.08" x2="12" y2="12"></line>
  </svg>`
};

const ShieldIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    <path d="M9 12l2 2 4-4"></path>
  </svg>`
};

const MapIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
    <line x1="8" y1="2" x2="8" y2="18"></line>
    <line x1="16" y1="6" x2="16" y2="22"></line>
  </svg>`
};

const ToolIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
  </svg>`
};

const ClipboardIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
    <path d="M9 14l2 2 4-4"></path>
  </svg>`
};

const HealthIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
  </svg>`
};

const WaterIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
  </svg>`
};

const MarketIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>`
};

const TrashIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>`
};

const AlcoholIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M8 22h8"></path>
    <path d="M12 11v11"></path>
    <path d="M5 3l5 7h4l5-7"></path>
    <path d="M6.5 7h11"></path>
    <path d="M10 10a4 4 0 1 0 4 0"></path>
  </svg>`
};

const FireIcon = {
  template: `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
  </svg>`
};

const GridIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>`
};

export default {
  name: "ServicesPage",
  components: {
    HeaderComponent,
    FooterComponent,
    BusinessIcon,
    HomeIcon,
    CarIcon,
    DocumentIcon,
    PrinterIcon,
    BuildingIcon,
    ShieldIcon,
    MapIcon,
    ToolIcon,
    ClipboardIcon,
    HealthIcon,
    WaterIcon,
    MarketIcon,
    TrashIcon,
    AlcoholIcon,
    FireIcon,
    GridIcon
  },
  data() {
    return {
      searchQuery: '',
      activeCategory: 'all',
      categories: [
        { id: 'all', name: 'All Services', icon: 'GridIcon' },
        { id: 'business', name: 'Business', icon: 'BusinessIcon' },
        { id: 'property', name: 'Property', icon: 'HomeIcon' },
        { id: 'transport', name: 'Transport', icon: 'CarIcon' },
        { id: 'health', name: 'Public Health', icon: 'HealthIcon' },
        { id: 'utilities', name: 'Utilities', icon: 'WaterIcon' },
        { id: 'other', name: 'Other', icon: 'ClipboardIcon' }
      ],
      services: [
        {
          id: 1,
          title: 'Business Registration',
          description: 'Register a new business and obtain your Single Business Permit to operate legally within the county.',
          icon: 'BusinessIcon',
          color: 'blue',
          category: 'business',
          link: '/create-business',
          features: ['New Registration', 'Permit Renewal', 'Business Transfer'],
          badge: 'Popular',
          featured: true
        },
        {
          id: 2,
          title: 'Print Permit',
          description: 'Download and print your business permit certificate after successful payment and approval.',
          icon: 'PrinterIcon',
          color: 'green',
          category: 'business',
          link: '/print-permit',
          features: ['Digital Certificate', 'Instant Download', 'Verify Permit']
        },
        {
          id: 3,
          title: 'Land Rates',
          description: 'Pay your annual land rates and property taxes. View your balance and payment history.',
          icon: 'HomeIcon',
          color: 'orange',
          category: 'property',
          link: '/land-rates',
          features: ['Check Balance', 'Pay Online', 'View Statement'],
          badge: 'Popular',
          featured: true
        },
        {
          id: 4,
          title: 'Property Self Service',
          description: 'Update your property information in the county valuation roll. Transfer ownership or update details.',
          icon: 'ShieldIcon',
          color: 'teal',
          category: 'property',
          link: '/property-self-service',
          features: ['Update Details', 'Transfer Ownership', 'Valuation Roll']
        },
        {
          id: 5,
          title: 'Daily Parking',
          description: 'Pay for daily street parking or off-street parking. Get instant digital receipts.',
          icon: 'CarIcon',
          color: 'purple',
          category: 'transport',
          link: '/daily-parking',
          features: ['Daily Parking', 'Flexible Duration', 'Instant Receipt'],
          badge: 'Popular',
          featured: true
        },
        {
          id: 6,
          title: 'Seasonal Parking',
          description: 'Apply for monthly or annual parking passes for regular parking needs.',
          icon: 'CarIcon',
          color: 'indigo',
          category: 'transport',
          link: '/seasonal-parking',
          features: ['Monthly Pass', 'Annual Pass', 'Reserved Spots']
        },
        {
          id: 7,
          title: 'House Rent',
          description: 'Pay your county house rent online. View your balance, arrears, and payment history.',
          icon: 'BuildingIcon',
          color: 'pink',
          category: 'property',
          link: '/house-rents',
          features: ['Pay Rent', 'View Arrears', 'Payment History']
        },
        {
          id: 8,
          title: 'Pay Bill',
          description: 'Look up and pay any county bill using your bill number. Quick and convenient.',
          icon: 'DocumentIcon',
          color: 'blue',
          category: 'other',
          link: '/pay-bill',
          features: ['Bill Lookup', 'Pay Any Bill', 'Print Receipt'],
          featured: true
        },
        {
          id: 9,
          title: 'Print Receipt',
          description: 'Retrieve and print receipts for any previous payment made on the platform.',
          icon: 'PrinterIcon',
          color: 'green',
          category: 'other',
          link: '/print-receipt',
          features: ['Find Receipt', 'Print Copy', 'Email Receipt']
        },
        {
          id: 10,
          title: 'Building Permits',
          description: 'Apply for construction and building permits. Submit plans and track approval status.',
          icon: 'ToolIcon',
          color: 'orange',
          category: 'property',
          link: '/building-permits',
          features: ['New Application', 'Track Status', 'Download Permit'],
          badge: 'Coming Soon'
        },
        {
          id: 12,
          title: 'Water Services',
          description: 'Pay water bills, apply for new connections, and report issues with water supply.',
          icon: 'WaterIcon',
          color: 'cyan',
          category: 'utilities',
          link: '/water-services',
          features: ['Pay Bills', 'New Connection', 'Report Issue'],
          badge: 'Coming Soon'
        },
        {
          id: 13,
          title: 'Market Stalls',
          description: 'Apply for market stall allocation and pay market fees.',
          icon: 'MarketIcon',
          color: 'amber',
          category: 'business',
          link: '/market-stalls',
          features: ['Stall Application', 'Pay Fees', 'Renewal']
        },
        {
          id: 14,
          title: 'Waste Management',
          description: 'Pay garbage collection fees and request special waste collection services.',
          icon: 'TrashIcon',
          color: 'slate',
          category: 'utilities',
          link: '/waste-management',
          features: ['Pay Fees', 'Request Pickup', 'Report Issue']
        },
        {
          id: 22,
          title: 'Park Fees',
          description: 'Pay for Amboseli National Park entry, camping, vehicle & activity fees. Instant M-Pesa payment.',
          icon: 'MapIcon',
          color: 'emerald',
          category: 'transport',
          link: '/park-fees',
          features: ['Adult / Child Entry', 'Camping & Vehicles', 'Activities & Passes'],
          badge: 'New'
        },
        {
          id: 15,
          title: 'Land Search',
          description: 'Conduct official land searches and obtain land ownership records.',
          icon: 'MapIcon',
          color: 'emerald',
          category: 'property',
          link: '/land-search',
          features: ['Official Search', 'Ownership Records', 'Land Maps']
        },
        {
          id: 16,
          title: 'Alcoholic Drinks Licence',
          description: 'Apply for liquor licenses to sell, distribute, or manufacture alcoholic beverages within the county.',
          icon: 'AlcoholIcon',
          color: 'wine',
          category: 'business',
          link: '/liquor-license',
          features: ['New Application', 'License Renewal', 'Transfer License'],
          badge: 'New',
          featured: true
        },
        {
          id: 17,
          title: 'Medical Certificate',
          description: 'Certificate of Medical Examination for food handlers. Required under Food, Drugs and Chemical Substances Act.',
          icon: 'HealthIcon',
          color: 'emerald',
          category: 'health',
          link: '/print-medical-certificate',
          features: ['Print Certificate', 'Track Application', 'Verify Status'],
          badge: 'New',
          featured: true
        },
        {
          id: 18,
          title: 'Food Hygiene Licence',
          description: 'Apply for food hygiene licences for restaurants, hotels, food vendors and other food establishments.',
          icon: 'ClipboardIcon',
          color: 'teal',
          category: 'health',
          link: '/health-certificates',
          features: ['New Application', 'Print Licence', 'Track Status']
        },
        {
          id: 19,
          title: 'Fire Inspection Certificate',
          description: 'Obtain fire safety inspection certificates for your business premises. Required for all commercial establishments.',
          icon: 'FireIcon',
          color: 'red',
          category: 'business',
          link: '/print-fire-certificate',
          features: ['Print Certificate', 'Track Application', 'Fire Safety Compliance'],
          badge: 'New',
          featured: true
        }
      ]
    };
  },

  computed: {
    moduleEnabledMap() { return moduleEnabledMap; },
    filteredServices() {
      let result = this.services;

      // Hide services whose target route belongs to a disabled module.
      // (Fail-open: services with no slug mapping and services whose slug isn't
      // in the loaded map yet are shown, so the very first paint isn't empty.)
      result = result.filter(s => {
        const slug = slugForPath(s.link);
        if (!slug) return true;
        const v = moduleEnabledMap[slug];
        return v === undefined ? true : v !== false;
      });

      // Filter by category
      if (this.activeCategory !== 'all') {
        result = result.filter(s => s.category === this.activeCategory);
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(s =>
            s.title.toLowerCase().includes(query) ||
            s.description.toLowerCase().includes(query) ||
            s.features.some(f => f.toLowerCase().includes(query))
        );
      }

      // Pin high-frequency services to the top so citizens see them first.
      // Ordering: Daily Parking, Seasonal Parking, Pay Bill, Park Fees, then
      // everything else in the array's original (id) order.
      const priorityLinks = ['/daily-parking', '/seasonal-parking', '/pay-bill', '/park-fees'];
      const priorityIndex = (s) => {
        const idx = priorityLinks.indexOf(s.link);
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
      };
      result = [...result].sort((a, b) => {
        const pa = priorityIndex(a);
        const pb = priorityIndex(b);
        if (pa !== pb) return pa - pb;
        return (a.id || 0) - (b.id || 0);
      });

      return result;
    }
  },

  mounted() {
    loadSSModules();
  },

  methods: {
    getCategoryCount(categoryId) {
      if (categoryId === 'all') return this.services.length;
      return this.services.filter(s => s.category === categoryId).length;
    },

    clearFilters() {
      this.searchQuery = '';
      this.activeCategory = 'all';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 18px;
  margin-bottom: 32px;
}

/* Hero Search */
.hero-search {
  max-width: 500px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-wrapper svg {
  position: absolute;
  left: 16px;
  color: #94a3b8;
}

.search-input {
  width: 100%;
  padding: 16px 48px;
  border: none;
  border-radius: 12px;
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  background: white;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.search-input:focus {
  outline: none;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
}

.search-input::placeholder {
  color: #94a3b8;
}

.clear-search {
  position: absolute;
  right: 12px;
  padding: 8px;
  background: #f1f5f9;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.clear-search:hover {
  background: #e2e8f0;
  color: #1e293b;
}

/* Filter Section */
.filter-section {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 16px 0;
  position: sticky;
  top: 72px;
  z-index: 100;
}

.filter-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.filter-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.filter-tab:hover {
  border-color: #0f4c81;
  color: #0f4c81;
}

.filter-tab.active {
  background: #0f4c81;
  border-color: #0f4c81;
  color: white;
}

.filter-tab svg {
  width: 18px;
  height: 18px;
}

.tab-count {
  padding: 2px 8px;
  background: rgba(0,0,0,0.1);
  border-radius: 10px;
  font-size: 12px;
}

.filter-tab.active .tab-count {
  background: rgba(255,255,255,0.2);
}

/* Services Section */
.services-section {
  padding: 48px 0;
}

.results-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  font-size: 14px;
  color: #64748b;
}

.clear-filters {
  background: none;
  border: none;
  color: #0f4c81;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.service-card {
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: all 0.3s;
  position: relative;
  border: 1px solid #e2e8f0;
}

.service-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -8px rgba(0,0,0,0.15);
  border-color: transparent;
}

.service-card.featured {
  border-color: #0f4c81;
  border-width: 2px;
}

.service-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 4px 12px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.service-icon {
  width: 60px;
  height: 60px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.service-icon.blue { background: #dbeafe; color: #2563eb; }
.service-icon.green { background: #d1fae5; color: #059669; }
.service-icon.orange { background: #ffedd5; color: #ea580c; }
.service-icon.purple { background: #ede9fe; color: #7c3aed; }
.service-icon.pink { background: #fce7f3; color: #db2777; }
.service-icon.teal { background: #ccfbf1; color: #0d9488; }
.service-icon.indigo { background: #e0e7ff; color: #4f46e5; }
.service-icon.red { background: #fee2e2; color: #dc2626; }
.service-icon.cyan { background: #cffafe; color: #0891b2; }
.service-icon.amber { background: #fef3c7; color: #d97706; }
.service-icon.slate { background: #f1f5f9; color: #475569; }
.service-icon.emerald { background: #d1fae5; color: #059669; }
.service-icon.wine { background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%); color: #9d174d; }

.service-card h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 10px;
}

.service-card > p {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 16px;
}

.service-features {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.service-features li {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #059669;
  background: #d1fae5;
  padding: 4px 10px;
  border-radius: 20px;
}

.service-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0f4c81;
  text-decoration: none;
  transition: gap 0.2s;
}

.service-link:hover {
  gap: 12px;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 80px 20px;
}

.no-results-icon {
  width: 100px;
  height: 100px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #94a3b8;
}

.no-results h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-results p {
  color: #64748b;
  margin-bottom: 24px;
}

/* Quick Access Section */
.quick-access-section {
  background: #f1f5f9;
  padding: 64px 0;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-badge {
  display: inline-block;
  padding: 6px 16px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
}

.section-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 16px;
  color: #64748b;
}

.quick-access-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.quick-access-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 14px;
  text-decoration: none;
  transition: all 0.3s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.quick-access-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.1);
}

.qa-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qa-icon.blue { background: #dbeafe; color: #2563eb; }
.qa-icon.green { background: #d1fae5; color: #059669; }
.qa-icon.purple { background: #ede9fe; color: #7c3aed; }
.qa-icon.orange { background: #ffedd5; color: #ea580c; }

.qa-content {
  flex: 1;
  min-width: 0;
}

.qa-content h4 {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}

.qa-content p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.qa-arrow {
  color: #94a3b8;
  flex-shrink: 0;
  transition: transform 0.2s;
}

.quick-access-card:hover .qa-arrow {
  transform: translateX(4px);
  color: #0f4c81;
}

/* Stats Section */
.stats-section {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
}

.stats-grid {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 48px;
}

.stat-item {
  text-align: center;
  color: white;
}

.stat-number {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  opacity: 0.8;
}

.stat-divider {
  width: 1px;
  height: 48px;
  background: rgba(255,255,255,0.2);
}

/* Help Section */
.help-section {
  padding: 64px 0;
}

.help-card {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  border-radius: 24px;
  padding: 48px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  overflow: hidden;
  position: relative;
}

.help-content h2 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: white;
  margin-bottom: 12px;
}

.help-content p {
  font-size: 16px;
  color: rgba(255,255,255,0.8);
  margin-bottom: 28px;
}

.help-actions {
  display: flex;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  border: none;
}

.btn-primary {
  background: #0f4c81;
  color: white;
}

.btn-primary:hover {
  background: #0a3459;
}

.btn-white {
  background: white;
  color: #0f4c81;
}

.btn-white:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.2);
}

.btn-outline-white {
  background: transparent;
  color: white;
  border: 2px solid rgba(255,255,255,0.3);
}

.btn-outline-white:hover {
  background: rgba(255,255,255,0.1);
  border-color: white;
}

/* Help Graphic */
.help-graphic {
  position: relative;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.graphic-circles {
  position: absolute;
  inset: 0;
}

.circle {
  position: absolute;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.1);
}

.circle-1 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 4s ease-in-out infinite;
}

.circle-2 {
  width: 150px;
  height: 150px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 4s ease-in-out infinite 1s;
}

.circle-3 {
  width: 100px;
  height: 100px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 4s ease-in-out infinite 2s;
}

@keyframes pulse {
  0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
}

.graphic-icon {
  position: relative;
  z-index: 1;
  width: 100px;
  height: 100px;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* Responsive */
@media (max-width: 1024px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .quick-access-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .help-card {
    grid-template-columns: 1fr;
    padding: 40px;
  }

  .help-graphic {
    display: none;
  }

  .stats-grid {
    gap: 32px;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 22px;
    line-height: 1.2;
  }
  .page-subtitle { font-size: 13px; }

  /* Compact 2-col tile grid on mobile (was full-width single column). */
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .service-card { padding: 14px 12px; border-radius: 12px; }
  .service-icon { width: 40px; height: 40px; border-radius: 10px; margin-bottom: 10px; }
  .service-icon svg { width: 20px; height: 20px; }
  .service-card h3 { font-size: 13.5px; margin-bottom: 4px; line-height: 1.2; }
  .service-card > p { font-size: 11.5px; line-height: 1.4; margin-bottom: 8px; }
  .service-features { display: none; }        /* feature bullets hidden on mobile */
  .service-badge { top: 8px; right: 8px; padding: 2px 8px; font-size: 9.5px; }
  .service-link {
    font-size: 12px;
    padding: 7px 10px;
    justify-content: center;
    gap: 4px;
  }
  .service-link svg { width: 12px; height: 12px; }

  .quick-access-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .filter-tabs {
    gap: 6px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    flex-wrap: nowrap;
    padding-bottom: 4px;
  }

  .filter-tab {
    padding: 6px 12px;
    font-size: 12px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .stats-grid {
    flex-wrap: wrap;
    gap: 24px;
  }

  .stat-divider {
    display: none;
  }

  .stat-item {
    flex: 1;
    min-width: 120px;
  }

  .stat-number {
    font-size: 28px;
  }

  .help-actions {
    flex-direction: column;
  }
}

/* Extra-narrow phones (<420px) — squeeze further so 2 tiles still fit
   cleanly on 360px viewports without the icons overlapping the text. */
@media (max-width: 420px) {
  .services-grid { gap: 8px; }
  .service-card { padding: 12px 10px; border-radius: 10px; }
  .service-icon { width: 36px; height: 36px; margin-bottom: 8px; border-radius: 9px; }
  .service-icon svg { width: 18px; height: 18px; }
  .service-card h3 { font-size: 12.5px; }
  .service-card > p { font-size: 11px; }
  .service-link { font-size: 11.5px; padding: 6px 9px; }
}
</style>